import { useState } from 'react';
import { useLangGraphInterruptState, useLangGraphSendCommand } from '@assistant-ui/react-langgraph';
import { HelpCircle, Send } from 'lucide-react';

import { Button } from '@accio3d/design-system/button';

import type { AgentPendingQuestion } from '../../api/agent';
import { reportAgentError } from './agent-error-store';

export function AgentPendingQuestion() {
  const interrupt = useLangGraphInterruptState();
  const sendCommand = useLangGraphSendCommand();
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);

  const pending = interrupt?.value as AgentPendingQuestion | undefined;
  if (!pending?.question) return null;

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || sending) return;

    setSending(true);
    void Promise.resolve(sendCommand({ resume: trimmed }))
      .then(() => setAnswer(''))
      .catch((error: unknown) => reportAgentError(error))
      .finally(() => setSending(false));
  };

  const choices = pending.choices ?? [];

  return (
    <div className="space-y-2 border-t border-border/50 bg-muted/20 p-3">
      <p className="flex items-start gap-1.5 text-xs text-foreground">
        <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span>{pending.question}</span>
      </p>

      {choices.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {choices.map((choice) => (
            <Button
              key={choice}
              size="sm"
              variant="secondary"
              className="h-7 text-xs"
              disabled={sending}
              onClick={() => submit(choice)}
            >
              {choice}
            </Button>
          ))}
        </div>
      ) : (
        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit(answer);
          }}
        >
          <label className="sr-only" htmlFor="agent-clarification">
            Your answer
          </label>
          <input
            id="agent-clarification"
            data-agent-autofocus=""
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            placeholder="Your answer…"
            autoComplete="off"
            readOnly={sending}
            className="h-9 flex-1 rounded-md border border-border/60 bg-background px-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
          <Button
            type="submit"
            size="icon"
            className="h-9 w-9"
            title="Send answer"
            disabled={sending}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      )}
    </div>
  );
}

export function useHasPendingQuestion(): boolean {
  const interrupt = useLangGraphInterruptState();
  return Boolean((interrupt?.value as AgentPendingQuestion | undefined)?.question);
}
